import { useGroupedQueuesStore, visibleQueuesSelector } from '../../store';
import { KanbanObject, KanbanProductionQueue, KanbanTaskView, TaskStatus } from '@nexelem/graphne-api';
import { Items, KanbanDnd, OnDragEnd } from '../dndComponents';
import { useKanbanEventStore } from '../store';
import { KanbanRenderer } from './KanbanRenderer';
import { getColumnId } from '../utils';
import { useSaveGanttDropTaskMutation } from '../../../../../queryHooks/productionPlanHooks';
import { useEffect, useMemo } from 'react';
import { KanbanTaskResolver } from './KanbanTaskResolver';
import {
  openKanbanInProgressToCompletedModal,
  openKanbanInProgressToPendingModal,
  openKanbanPendingToInProgressModal,
} from '../modals';
import { NodeType } from '../../Gantt/types';
import { useProductionPlanQueryParams } from '../../hooks';
import { useProductionTaskLocate } from '../../Gantt/hooks';
import {
  closeTaskDetailsDrawer,
  openTaskDetailsDrawer,
} from '../../../../../pages/generic/Products/components/TaskDetailDrawer/TaskDetailsDrawer';

export const KanbanBoard = () => {
  const saveDropTaskMutation = useSaveGanttDropTaskMutation();
  const visibleQueues = useGroupedQueuesStore(visibleQueuesSelector);
  const items = useKanbanEventStore((state) => state.data?.productionQueues);
  const { queryParams } = useProductionPlanQueryParams();
  const locateTask = useProductionTaskLocate();

  const sortedItems = useMemo(
    () =>
      visibleQueues
        .map(({ id }) => items?.find(({ queue }) => queue.id === id))
        .filter((queue): queue is KanbanProductionQueue => !!queue)
        .map(({ queue, tasks }) => {
          return {
            queueId: queue.id,
            pending: getTasksByStatus(tasks, TaskStatus.Pending),
            inProgress: getTasksByStatus(tasks, TaskStatus.InProgress),
            completed: getTasksByStatus(tasks, TaskStatus.Completed),
          };
        })
        .reduce((acc, { queueId, inProgress, pending, completed }) => {
          return {
            ...acc,
            [getColumnId(queueId, TaskStatus.Pending)]: pending,
            [getColumnId(queueId, TaskStatus.InProgress)]: inProgress,
            [getColumnId(queueId, TaskStatus.Completed)]: completed,
          };
        }, {} as Items),
    [visibleQueues, items],
  );

  useEffect(() => {
    if (queryParams.taskId && queryParams.queueId) {
      locateTask({
        queueId: queryParams.queueId,
        taskId: queryParams.taskId,
      });
    }
    // only on first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (queryParams.taskId) {
      setTimeout(() => {
        openTaskDetailsDrawer({
          taskId: queryParams?.taskId?.toString() || '',
        });
      }, 0);
    }

    return () => {
      closeTaskDetailsDrawer();
    };
    // we need open drawer only once page opened with taskId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDragEnd: OnDragEnd = (move) => {
    if (move.type === 'sort' && move.queueId) {
      saveDropTaskMutation.mutate({
        taskId: move.id,
        predecessorTaskId: move.afterId,
        queueId: move.queueId,
        type: NodeType.TASK,
      });
    }

    if (move.type === 'status') {
      if (move.from === 'Pending' && move.to === 'InProgress') {
        openKanbanPendingToInProgressModal({ id: move.id });
      }

      if (move.from === 'InProgress' && move.to === 'Pending') {
        openKanbanInProgressToPendingModal({ id: move.id });
      }

      if (move.from === 'InProgress' && move.to === 'Completed') {
        openKanbanInProgressToCompletedModal({ id: move.id });
      }
    }
  };

  return (
    <KanbanDnd items={sortedItems} OverlayComponent={KanbanTaskResolver} onDragEnd={onDragEnd}>
      {KanbanRenderer}
    </KanbanDnd>
  );
};

// Type only for getTasksByStatus
type KanbanTaskHelperType = Pick<KanbanTaskView, 'id' | 'start'> & { status?: TaskStatus };

const getTasksByStatus = (tasks: KanbanObject[] = [], taskStatus: TaskStatus) =>
  tasks
    .map((task): KanbanTaskHelperType => {
      if (task.tpe === 'Task') {
        return task;
      } else {
        return {
          id: task.batchId,
          start: task.start,
          status: task.tasks?.[0]?.status,
        };
      }
    })
    .filter(({ status }) => status === taskStatus)
    .toSorted(sortByStartDate)
    .map(({ id }) => id);

const sortByStartDate = (a: KanbanTaskHelperType, b: KanbanTaskHelperType) =>
  new Date(a.start).getTime() - new Date(b.start).getTime();
